import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Check, Sparkles, CreditCard, Zap, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [selectedPack, setSelectedPack] = useState("");
  const [loading, setLoading] = useState(false);
  const [creditPacks, setCreditPacks] = useState([]);
  const [activeTab, setActiveTab] = useState("subscription");

  useEffect(() => {
    fetchCreditPacks();
  }, []);

  const fetchCreditPacks = async () => {
    try {
      const response = await axios.get(`${API}/credit-packs`);
      setCreditPacks(response.data.packs);
    } catch (error) { console.error("Failed to fetch credit packs"); }
  };

  const plans = [
    {
      id: "monthly",
      name: "Monthly",
      price: 24.99,
      period: "month",
      features: [
        "Unlimited AI room visualisations",
        "Priority AI design consultation",
        "Advanced product recommendations",
        "Style Consistency across rooms",
        "Cancel anytime"
      ]
    },
    {
      id: "annual",
      name: "Annual",
      price: 249.99,
      period: "year",
      savings: "Save £49.89",
      features: [
        "Everything in Monthly",
        "2 months free",
        "Exclusive annual member discounts",
        "Priority customer support",
        "Free delivery on all orders"
      ]
    }
  ];

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        tier: selectedPlan,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create subscription");
      setLoading(false);
    }
  };

  const handleBuyCredits = async () => {
    if (!selectedPack) { toast.error("Please select a credit pack"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/credit-packs/checkout`, {
        pack_id: selectedPack,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process purchase");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#F06570]">Plans & Pricing</span>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A] mt-4">
            Unlock AI Design Tools
          </h1>
          <p className="text-[#5C5D5C] mt-4 max-w-xl mx-auto font-light">
            Every user gets <strong>4 free room designs</strong>. Need more? Choose a credit pack or subscribe for unlimited access.
          </p>
        </div>

        {/* Current Status */}
        {user?.subscription_tier && (
          <div className="bg-[#20C997]/10 border border-[#20C997] p-6 mb-8 text-center">
            <p className="font-medium text-[#20C997]">
              You're subscribed to the {user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1)} plan
            </p>
            <p className="text-sm text-[#5C5D5C] mt-1">
              Expires: {new Date(user.subscription_expires).toLocaleDateString("en-GB")}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex justify-centre gap-4 mb-10 border-b border-[#EAE6DF]">
          <button onClick={() => setActiveTab("subscription")} className={`pb-4 px-4 font-medium transition-colours flex items-centre gap-2 ${activeTab === "subscription" ? "text-[#20C997] border-b-2 border-[#20C997]" : "text-[#5C5D5C]"}`} data-testid="tab-subscription">
            <Crown size={18} /> Professional Subscription
          </button>
          <button onClick={() => setActiveTab("credits")} className={`pb-4 px-4 font-medium transition-colours flex items-centre gap-2 ${activeTab === "credits" ? "text-[#20C997] border-b-2 border-[#20C997]" : "text-[#5C5D5C]"}`} data-testid="tab-credits">
            <Zap size={18} /> Image Credits
          </button>
        </div>

        {/* Subscription Tab */}
        {activeTab === "subscription" && (
          <>
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="subscription-plans">
              {plans.map((plan) => (
                <div key={plan.id} onClick={() => setSelectedPlan(plan.id)} className={`p-8 border-2 cursor-pointer transition-all ${selectedPlan === plan.id ? "border-[#20C997] bg-white" : "border-[#EAE6DF] hover:border-[#20C997]/50"}`} data-testid={`plan-${plan.id}`}>
                  {plan.savings && (
                    <span className="inline-block px-3 py-1 bg-[#F06570] text-white text-xs font-medium mb-4">{plan.savings}</span>
                  )}
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A]">{plan.name}</h3>
                  <div className="mt-4">
                    <span className="text-4xl font-light text-[#1A1B1A]">£{plan.price}</span>
                    <span className="text-[#5C5D5C]">/{plan.period}</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-[#5C5D5C]">
                        <Check size={18} className="text-[#20C997] flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <Button onClick={handleSubscribe} disabled={loading} className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-6 text-base font-medium" data-testid="subscribe-btn">
              <CreditCard size={20} className="mr-2" />
              {loading ? "Processing..." : `Subscribe to ${selectedPlan === "monthly" ? "Monthly" : "Annual"} Plan`}
            </Button>
          </>
        )}

        {/* Credits Tab */}
        {activeTab === "credits" && (
          <>
            <div className="text-center mb-8">
              <p className="text-[#5C5D5C] font-light max-w-lg mx-auto">
                Purchase image credits for individual room designs. Choose lifetime credits that never expire, or monthly packs for short-term projects.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="credit-packs">
              {/* Lifetime Packs */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1A1B1A] mb-4">Lifetime Access</h3>
                {creditPacks.filter(p => p.id.startsWith("lifetime")).map(pack => (
                  <div key={pack.id} onClick={() => setSelectedPack(pack.id)} className={`p-6 border-2 cursor-pointer transition-all mb-4 ${selectedPack === pack.id ? "border-[#20C997] bg-white" : "border-[#EAE6DF] hover:border-[#20C997]/50"}`} data-testid={`pack-${pack.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#1A1B1A]">{pack.credits} Credits</p>
                        <p className="text-xs text-[#5C5D5C] mt-1">Never expire</p>
                      </div>
                      <p className="text-2xl font-light text-[#1A1B1A]">£{pack.price}</p>
                    </div>
                    <p className="text-xs text-[#8E8F8E] mt-2">£{(pack.price / pack.credits).toFixed(2)} per design</p>
                  </div>
                ))}
              </div>

              {/* Monthly Packs */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1A1B1A] mb-4">30-Day Access</h3>
                {creditPacks.filter(p => p.id.startsWith("monthly")).map(pack => (
                  <div key={pack.id} onClick={() => setSelectedPack(pack.id)} className={`p-6 border-2 cursor-pointer transition-all mb-4 ${selectedPack === pack.id ? "border-[#20C997] bg-white" : "border-[#EAE6DF] hover:border-[#20C997]/50"}`} data-testid={`pack-${pack.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#1A1B1A]">{pack.credits} Credits</p>
                        <p className="text-xs text-[#5C5D5C] mt-1">Valid for 30 days</p>
                      </div>
                      <p className="text-2xl font-light text-[#1A1B1A]">£{pack.price}</p>
                    </div>
                    <p className="text-xs text-[#8E8F8E] mt-2">£{(pack.price / pack.credits).toFixed(2)} per design</p>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleBuyCredits} disabled={loading || !selectedPack} className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-6 text-base font-medium" data-testid="buy-credits-btn">
              <Zap size={20} className="mr-2" />
              {loading ? "Processing..." : "Purchase Credits"}
            </Button>
          </>
        )}

        <p className="text-xs text-[#8E8F8E] text-center mt-4">Secure checkout powered by Stripe. Cancel anytime.</p>

        {/* Features */}
        <div className="mt-16">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] text-center mb-8">What You Get</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-[#20C997]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-[#20C997]" size={24} />
              </div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">AI Room Visualisations</h3>
              <p className="text-sm text-[#5C5D5C]">Generate photorealistic room designs with products from our marketplace</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-[#F06570]/10 flex items-center justify-center mx-auto mb-4">
                <svg className="text-[#F06570]" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              </div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">Style Consistency</h3>
              <p className="text-sm text-[#5C5D5C]">Set colours and materials that carry across every room in your property</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-[#3E6656]/10 flex items-center justify-center mx-auto mb-4">
                <svg className="text-[#3E6656]" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>
              </div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">2D Floor Plan Analysis</h3>
              <p className="text-sm text-[#5C5D5C]">Describe your room layout and get furniture positioned accurately</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
